import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import { Form } from 'react-bootstrap';
import Select from 'react-select/creatable';

import {
  getURL,
  GLOBAL_VENDORS_CHAIN_LOCATIONS,
  GLOBAL_VENDORS_FOR_DROPDOWN,
} from 'lib/networking/endpoints';
import { P0SyncAttributeOptions } from 'lib/enums/P0Attributes';
import SearchableScrollableMultiSelect from 'components/shared/SearchableScrollableMultiSelect';
import useGet from 'lib/hooks/useGet';
import { VENDOR_FIELDS } from 'components/manage_vendors/vendor_details/VendorDetailSplitView';
import { VendorCategoryOptions } from 'lib/enums/VendorCategory';

const EXCLUDED_CND_FIELDS_OPTIONS = [{ label: 'status', value: 'status' }];

function EditableProperty({ label, vendor, setIsEditing, setUpdatedVendor }) {
  const { data: chainLocationsResponse } = useGet(
    getURL(GLOBAL_VENDORS_CHAIN_LOCATIONS, { vendor_id: vendor.id }),
    {},
    label !== VENDOR_FIELDS.PRIMARY_SOURCE,
  );
  const chainLocationOptions = (chainLocationsResponse?.data || []).map(location => ({
    label: location.name,
    value: location.id,
  }));

  const [selectedOptions, setSelectedOptions] = useState(() => {
    if (!vendor[label]) {
      return [];
    }

    let selectedValues = null;
    if (label !== 'chains' && label !== VENDOR_FIELDS.PRIMARY_SOURCE) {
      selectedValues = vendor[label] instanceof Array ? vendor[label] : vendor[label].split(',');
    }

    const optionsForSelectedValues = () => {
      if (label === 'excluded_p0_sync_attributes') {
        return P0SyncAttributeOptions.filter(option => selectedValues.includes(option.value));
      } else if (label === 'rejected_brand_names') {
        return selectedValues.map(val => ({ label: val, value: val }));
      } else if (label === 'chains') {
        return vendor[label].map(val => ({ label: val.name, value: val.id }));
      } else if (label === 'excluded_cnd_sync_fields') {
        return EXCLUDED_CND_FIELDS_OPTIONS.filter(option => selectedValues.includes(option.value));
      } else if (label === 'excluded_propagation_attributes') {
        return selectedValues.map(val => ({ label: val, value: val }));
      } else if (label === 'alliance') {
        return vendor[label].map(val => ({ label: val, value: val }));
      } else if (label === 'third_party') {
        return vendor[label].map(val => ({ label: val, value: val }));
      } else if (label === 'vendor_category') {
        return VendorCategoryOptions.filter(option => selectedValues.includes(option.value));
      } else if (label === VENDOR_FIELDS.PRIMARY_SOURCE) {
        return chainLocationOptions.filter(option => option.value === vendor[label]);
      }
    };
    return optionsForSelectedValues();
  });

  useEffect(() => {
    if (label === VENDOR_FIELDS.PRIMARY_SOURCE && vendor[VENDOR_FIELDS.PRIMARY_SOURCE]) {
      setSelectedOptions(
        chainLocationOptions.filter(
          option => option.value === vendor[VENDOR_FIELDS.PRIMARY_SOURCE],
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainLocationsResponse]);

  const handleUpdateVendor = (property, value) => {
    setUpdatedVendor(prevVendor => ({
      ...prevVendor,
      [property]: value,
    }));
  };

  const handleSelectChange = selectedOptions => {
    setSelectedOptions(selectedOptions);
    if (label === 'chains') {
      const selectedValues = selectedOptions.map(option => ({
        name: option.label,
        id: option.value,
      }));
      handleUpdateVendor(label, selectedValues);
    } else if (label === 'vendor_category') {
      handleUpdateVendor(label, selectedOptions?.value || '');
    } else if (label === VENDOR_FIELDS.PRIMARY_SOURCE) {
      // GlobalVendor.update_by_id treats `None`/omitted as "leave unchanged", so clearing
      // must send 0 (falsy but not None) for the backend to actually clear the value.
      handleUpdateVendor(label, selectedOptions?.value || 0);
    } else if (label === 'alliance') {
      const selectedValues = selectedOptions.map(option => option.value);
      handleUpdateVendor(label, selectedValues);
    } else if (label === 'third_party') {
      const selectedValues = selectedOptions.map(option => option.value);
      handleUpdateVendor(label, selectedValues);
    } else {
      const selectedValuesString = selectedOptions.map(option => option.value).join(',');
      handleUpdateVendor(label, selectedValuesString);
    }
  };

  const optionsMap = {
    excluded_p0_sync_attributes: P0SyncAttributeOptions,
    excluded_cnd_sync_fields: EXCLUDED_CND_FIELDS_OPTIONS,
    excluded_propagation_attributes: [],
    rejected_brand_names: [],
    vendor_category: VendorCategoryOptions,
    alliance: [],
    third_party: [],
  };

  return [
    'excluded_p0_sync_attributes',
    'excluded_cnd_sync_fields',
    'excluded_propagation_attributes',
    'rejected_brand_names',
    'vendor_category',
    'alliance',
    'third_party',
  ].includes(label) ? (
    <Select
      className={css(styles.select)}
      autoFocus
      options={optionsMap[label]}
      isMulti={label !== 'vendor_category'}
      value={selectedOptions}
      onChange={handleSelectChange}
      isClearable={label === 'vendor_category'}
    />
  ) : 'chains' === label ? (
    <SearchableScrollableMultiSelect
      className={css(styles.select)}
      onChange={handleSelectChange}
      fetchUrl={GLOBAL_VENDORS_FOR_DROPDOWN}
      optionMapper={vendor => ({ label: vendor.name, value: vendor.id })}
      initOptions={selectedOptions}
    />
  ) : VENDOR_FIELDS.PRIMARY_SOURCE === label ? (
    <Select
      className={css(styles.select)}
      autoFocus
      options={chainLocationOptions}
      isMulti={false}
      isClearable
      value={selectedOptions}
      onChange={handleSelectChange}
    />
  ) : (
    <Form.Control
      className={css(styles.form)}
      autoFocus
      defaultValue={vendor[label]}
      onChange={event => handleUpdateVendor(label, event.target.value)}
      onKeyDown={e => {
        if (e.key === 'Escape') {
          setIsEditing(false);
        }
      }}
    />
  );
}

const styles = StyleSheet.create({
  select: {
    flexGrow: 0.96,
  },
  form: {
    height: 'auto',
    marginRight: '.6rem',
  },
});

EditableProperty.propTypes = {
  label: PropTypes.string,
  vendor: PropTypes.object,
  setIsEditing: PropTypes.func,
  setUpdatedVendor: PropTypes.func,
};

export default EditableProperty;
